import { useEffect, useRef } from 'react';

/** A deletion that has been asked for and not yet confirmed. */
export interface PendingDelete {
  /** The DELETE route to call once confirmed. */
  path: string;
  title: string;
  /** What will go, in the person's own terms. */
  detail: string;
  /** The success notice, once it has gone. */
  message: string;
  /** True when what is being deleted is the page itself. */
  leaves?: boolean;
}

/**
 * Asks before a deletion goes through.
 *
 * Written rather than reaching for `window.confirm`, which cannot be relied
 * on: an embedded webview suppresses it, and Chrome suppresses it for the
 * rest of the page as soon as somebody ticks "prevent this page from
 * creating additional dialogs". A suppressed `confirm` returns false without
 * showing anything, so the button it guards does nothing, says nothing, and
 * looks broken.
 *
 * Built on the same `.modal` markup the content previews use, so it inherits
 * the backdrop, the focus ring and the Escape key rather than inventing a
 * second kind of dialog.
 */
export function ConfirmDialog({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: PendingDelete;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const cancelButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    // Focus goes to Cancel, not Delete: the dialog appears under whatever the
    // person was about to press next, and Enter should not finish a deletion
    // they have not read.
    cancelButton.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="modal__backdrop" role="dialog" aria-modal="true" onClick={onCancel}>
      <div
        className="modal"
        style={{ maxWidth: 460 }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal__head">
          <span className="modal__title">{pending.title}</span>
          <button className="modal__close" onClick={onCancel} aria-label="Cancel">
            ×
          </button>
        </div>

        <div className="modal__body">
          <p style={{ margin: 0 }}>{pending.detail}</p>
          <p className="muted" style={{ fontSize: 13 }}>
            This cannot be undone from here.
          </p>
          <div className="row" style={{ marginTop: 16 }}>
            <button className="btn btn--sm" type="button" onClick={() => void onConfirm()}>
              Delete
            </button>
            <button
              ref={cancelButton}
              className="btn btn--ghost btn--sm"
              type="button"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
